import type PropertyFeeModel from 'uplisting-frontend/models/property-fee';

export interface ILanguageOption {
  code: string;
  label: string;
}

// BCL (Booking.com Language Codes)
// https://developers.booking.com/connectivity/docs/codes-bcl
export const BDC_LANGUAGES: ILanguageOption[] = [
  { code: 'en-gb', label: 'English (UK)' },
  { code: 'en-us', label: 'English (American)' },
  { code: 'ar', label: 'Arabic' },
  { code: 'bg', label: 'Bulgarian' },
  { code: 'ca', label: 'Catalan' },
  { code: 'cs', label: 'Czech' },
  { code: 'da', label: 'Danish' },
  { code: 'de', label: 'German' },
  { code: 'el', label: 'Greek' },
  { code: 'es', label: 'Spanish' },
  { code: 'es-ar', label: 'Spanish (Argentine)' },
  { code: 'es-mx', label: 'Spanish (Mexican)' },
  { code: 'et', label: 'Estonian' },
  { code: 'fi', label: 'Finnish' },
  { code: 'fr', label: 'French' },
  { code: 'he', label: 'Hebrew' },
  { code: 'hi', label: 'Hindi' },
  { code: 'hr', label: 'Croatian' },
  { code: 'hu', label: 'Hungarian' },
  { code: 'id', label: 'Indonesian' },
  { code: 'is', label: 'Icelandic' },
  { code: 'it', label: 'Italian' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ka', label: 'Georgian' },
  { code: 'ko', label: 'Korean' },
  { code: 'lt', label: 'Lithuanian' },
  { code: 'lv', label: 'Latvian' },
  { code: 'ms', label: 'Malay' },
  { code: 'nl', label: 'Dutch' },
  { code: 'no', label: 'Norwegian' },
  { code: 'pl', label: 'Polish' },
  { code: 'pt-br', label: 'Portuguese (Brazilian)' },
  { code: 'pt-pt', label: 'Portuguese' },
  { code: 'ro', label: 'Romanian' },
  { code: 'ru', label: 'Russian' },
  { code: 'sk', label: 'Slovak' },
  { code: 'sl', label: 'Slovenian' },
  { code: 'sr', label: 'Serbian' },
  { code: 'sv', label: 'Swedish' },
  { code: 'th', label: 'Thai' },
  { code: 'tl', label: 'Tagalog' },
  { code: 'tr', label: 'Turkish' },
  { code: 'uk', label: 'Ukrainian' },
  { code: 'vi', label: 'Vietnamese' },
  { code: 'zh-cn', label: 'Chinese (Simplified)' },
  { code: 'zh-tw', label: 'Chinese (Traditional)' },
];

export interface ICountryOption {
  code: string;
  name: string;
}

// Booking.com supported countries
// Sourced from BDC Contracting API country list
export const BDC_COUNTRIES: ICountryOption[] = [
  { code: 'AD', name: 'Andorra' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AI', name: 'Anguilla' },
  { code: 'AL', name: 'Albania' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AO', name: 'Angola' },
  { code: 'AQ', name: 'Antarctica' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'AT', name: 'Austria' },
  { code: 'AU', name: 'Australia' },
  { code: 'AW', name: 'Aruba' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BI', name: 'Burundi' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BL', name: 'Saint Barthelemy' },
  { code: 'BM', name: 'Bermuda' },
  { code: 'BN', name: 'Brunei Darussalam' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BQ', name: 'Bonaire, Saint Eustatius and Saba' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BV', name: 'Bouvet Island' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BZ', name: 'Belize' },
  { code: 'CA', name: 'Canada' },
  { code: 'CC', name: 'Cocos (Keeling) Islands' },
  { code: 'CD', name: 'Congo (Kinshasa)' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'CG', name: 'Congo (Brazzaville)' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'CI', name: "Cote d'Ivoire" },
  { code: 'CK', name: 'Cook Islands' },
  { code: 'CL', name: 'Chile' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CV', name: 'Cape Verde' },
  { code: 'CW', name: 'Curaçao' },
  { code: 'CX', name: 'Christmas Island' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DE', name: 'Germany' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EE', name: 'Estonia' },
  { code: 'EG', name: 'Egypt' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'ES', name: 'Spain' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'FK', name: 'Falkland Islands' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'FO', name: 'Faroe Islands' },
  { code: 'FR', name: 'France' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GE', name: 'Georgia' },
  { code: 'GF', name: 'French Guiana' },
  { code: 'GG', name: 'Guernsey' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GI', name: 'Gibraltar' },
  { code: 'GL', name: 'Greenland' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GP', name: 'Guadeloupe' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'GR', name: 'Greece' },
  { code: 'GS', name: 'South Georgia and South Sandwich Islands' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GU', name: 'Guam' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HM', name: 'Heard and McDonald Islands' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HR', name: 'Croatia' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HU', name: 'Hungary' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' },
  { code: 'IM', name: 'Isle of Man' },
  { code: 'IN', name: 'India' },
  { code: 'IO', name: 'British Indian Ocean Territory' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IR', name: 'Iran' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IT', name: 'Italy' },
  { code: 'JE', name: 'Jersey' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JO', name: 'Jordan' },
  { code: 'JP', name: 'Japan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'KM', name: 'Comoros' },
  { code: 'KN', name: 'Saint Kitts and Nevis' },
  { code: 'KP', name: 'Korea, North' },
  { code: 'KR', name: 'Korea, South' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KY', name: 'Cayman Islands' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'LA', name: 'Laos' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LY', name: 'Libya' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MD', name: 'Moldova' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MF', name: 'Saint Martin' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MK', name: 'Macedonia' },
  { code: 'ML', name: 'Mali' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'MO', name: 'Macau' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'MQ', name: 'Martinique' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MS', name: 'Montserrat' },
  { code: 'MT', name: 'Malta' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'MV', name: 'Maldives' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MX', name: 'Mexico' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NC', name: 'New Caledonia' },
  { code: 'NE', name: 'Niger' },
  { code: 'NF', name: 'Norfolk Island' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NO', name: 'Norway' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NU', name: 'Niue' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'OM', name: 'Oman' },
  { code: 'PA', name: 'Panama' },
  { code: 'PE', name: 'Peru' },
  { code: 'PF', name: 'French Polynesia' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PL', name: 'Poland' },
  { code: 'PM', name: 'Saint Pierre and Miquelon' },
  { code: 'PN', name: 'Pitcairn' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PT', name: 'Portugal' },
  { code: 'PW', name: 'Palau' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RE', name: 'Reunion' },
  { code: 'RO', name: 'Romania' },
  { code: 'RS', name: 'Serbia' },
  { code: 'RU', name: 'Russian Federation' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SB', name: 'Solomon Islands' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SE', name: 'Sweden' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SH', name: 'Saint Helena' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SJ', name: 'Svalbard and Jan Mayen Islands' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SM', name: 'San Marino' },
  { code: 'SN', name: 'Senegal' },
  { code: 'SO', name: 'Somalia' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'ST', name: 'Sao Tome and Principe' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'SX', name: 'Sint Maarten' },
  { code: 'SY', name: 'Syrian Arab Republic' },
  { code: 'SZ', name: 'Swaziland' },
  { code: 'TC', name: 'Turks and Caicos Islands' },
  { code: 'TD', name: 'Chad' },
  { code: 'TF', name: 'French Southern Territories' },
  { code: 'TG', name: 'Togo' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TK', name: 'Tokelau' },
  { code: 'TL', name: 'East Timor' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'UG', name: 'Uganda' },
  { code: 'UM', name: 'United States Minor Outlying Islands' },
  { code: 'US', name: 'United States of America' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VA', name: 'Vatican City' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VG', name: 'Virgin Islands, British' },
  { code: 'VI', name: 'Virgin Islands, U.S.' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'WF', name: 'Wallis and Futuna Islands' },
  { code: 'WS', name: 'Samoa' },
  { code: 'XA', name: 'Abkhazia' },
  { code: 'XC', name: 'Crimea' },
  { code: 'XK', name: 'Kosovo' },
  { code: 'XY', name: 'Northern Cyprus' },
  { code: 'YE', name: 'Yemen' },
  { code: 'YT', name: 'Mayotte' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
];

export interface IPhoneTypeOption {
  code: number;
  label: string;
}

export const BDC_PHONE_TYPES: IPhoneTypeOption[] = [
  { code: 1, label: 'Phone' },
  { code: 2, label: 'Fax' },
  { code: 3, label: 'Mobile' },
];

export interface BdcPolicyGroup {
  key: string;
  options: number[];
}

export const BDC_CANCELLATION_POLICY_GROUPS: BdcPolicyGroup[] = [
  { key: 'non_refundable', options: [1] },
  { key: 'fully_flexible', options: [152] },
  { key: 'after_reservation', options: [166, 168] },
  {
    key: 'before_arrival_first_night',
    options: [
      36, 34, 33, 173, 60, 250, 37, 262, 179, 30, 192, 563, 206, 564, 70, 566,
      113, 568, 117, 268, 548, 549,
    ],
  },
  {
    key: 'before_arrival_50',
    options: [
      52, 55, 58, 62, 560, 65, 263, 181, 68, 196, 203, 208, 565, 72, 567, 115,
      569, 119, 561,
    ],
  },
  {
    key: 'before_arrival_100',
    options: [
      38, 29, 31, 242, 16, 249, 15, 261, 178, 14, 172, 202, 205, 13, 74, 231,
      12, 237, 121, 267,
    ],
  },
  {
    key: 'on_day_of_arrival',
    options: [35, 182, 183, 43, 45, 47, 163, 200, 562, 42, 41, 49, 170],
  },
];

export const BDC_CANCELLATION_POLICY_CODES =
  BDC_CANCELLATION_POLICY_GROUPS.flatMap((g) => g.options);

export const UNSUPPORTED_PROPERTY_COUNTRY_CODES = ['BR', 'FR', 'JP'];
export const UNSUPPORTED_PROPERTY_STATES = ['Quebec', 'Québec', 'QC'];

export const BDC_CONNECT_WIDGET_URL =
  'https://admin.booking.com/?groups_redirect=%2Fhotel%2Fhoteladmin%2Fextranet_ng%2Fmanage%2Fchannel-manager%2Fwidget.html?provider_id=994';

export const bdcExtranetUrl = (hotelId: string): string =>
  `https://admin.booking.com/hotel/hoteladmin/extranet_ng/manage/home.html?hotel_id=${hotelId}`;

export const BDC_LEGAL_ENTITY_ID_ERRORS = [
  'invalid_format',
  'leid_not_found',
  'already_connected',
];

export const BDC_CONTRACT_CHECK_ERRORS = [
  'partner_card_not_found',
  'partner_not_connected',
  'channel_unavailable',
];

// Source: GET https://supply-xml.booking.com/hotels/xml/countrycurrencies
// Dual-currency alternates: https://developers.booking.com/connectivity/docs/b_xml-currencies
export const BDC_CURRENCY_COUNTRIES: Record<string, string[]> = {
  AED: ['AE'],
  AFN: ['AF'],
  AMD: ['AM'],
  ARS: ['AR'],
  AUD: ['AU', 'CC', 'KI', 'NF', 'NR', 'SB', 'TV'],
  AZN: ['AZ'],
  BHD: ['BH'],
  BRL: ['BR'],
  BWP: ['BW'],
  CAD: ['CA'],
  CHF: ['CH', 'LI'],
  CLP: ['CL'],
  CNY: ['CN'],
  COP: ['CO'],
  CVE: ['CV'],
  CZK: ['CZ'],
  DKK: ['DK', 'FO', 'GL'],
  DZD: ['DZ'],
  EUR: [
    'AD',
    'AL',
    'AT',
    'BA',
    'BE',
    'BF',
    'BG',
    'BJ',
    'BL',
    'CY',
    'CZ',
    'DE',
    'EE',
    'ES',
    'FI',
    'FR',
    'GA',
    'GF',
    'GM',
    'GN',
    'GP',
    'GQ',
    'GR',
    'GW',
    'HR',
    'HU',
    'IE',
    'IS',
    'IT',
    'KM',
    'LT',
    'LU',
    'LV',
    'LY',
    'MA',
    'MC',
    'MD',
    'ME',
    'MF',
    'MG',
    'MK',
    'MQ',
    'MR',
    'MT',
    'MU',
    'NE',
    'NL',
    'PM',
    'PT',
    'RE',
    'RO',
    'RS',
    'SC',
    'SI',
    'SK',
    'SM',
    'ST',
    'TG',
    'TN',
    'TR',
    'UA',
    'YT',
  ],
  FJD: ['FJ'],
  GBP: ['FK', 'GB', 'GI', 'IM', 'SH'],
  GEL: ['GE'],
  GTQ: ['GT'],
  HKD: ['HK', 'MO'],
  HUF: ['HU'],
  IDR: ['ID'],
  ILS: ['IL'],
  INR: ['IN'],
  IRR: ['IR'],
  ISK: ['IS'],
  JOD: ['JO'],
  JPY: ['JP'],
  KRW: ['KR'],
  KWD: ['KW'],
  KZT: ['KZ'],
  LSL: ['LS'],
  MAD: ['MA'],
  MDL: ['MD'],
  MOP: ['MO'],
  MXN: ['MX'],
  MYR: ['MY'],
  MZN: ['MZ'],
  NAD: ['NA'],
  NGN: ['NG'],
  NOK: ['NO'],
  NZD: ['CK', 'NU', 'NZ'],
  OMR: ['OM'],
  PGK: ['PG'],
  PHP: ['PH'],
  PKR: ['PK'],
  PLN: ['PL'],
  QAR: ['QA'],
  RON: ['RO'],
  RUB: ['RU'],
  SAR: ['SA'],
  SEK: ['SE'],
  SGD: ['BN', 'SG'],
  SLL: ['SL'],
  SZL: ['SZ'],
  THB: ['TH'],
  TND: ['TN'],
  TRY: ['TR'],
  TWD: ['TW'],
  UAH: ['UA'],
  USD: [
    'AF',
    'AG',
    'AI',
    'AO',
    'AR',
    'AS',
    'AW',
    'BB',
    'BD',
    'BI',
    'BM',
    'BO',
    'BQ',
    'BR',
    'BS',
    'BT',
    'BY',
    'BZ',
    'CD',
    'CL',
    'CO',
    'CR',
    'CU',
    'CW',
    'DJ',
    'DM',
    'DO',
    'EC',
    'EG',
    'ER',
    'ET',
    'FM',
    'GA',
    'GD',
    'GE',
    'GH',
    'GN',
    'GT',
    'GU',
    'GY',
    'HN',
    'HT',
    'ID',
    'IL',
    'IQ',
    'JM',
    'KE',
    'KG',
    'KH',
    'KN',
    'KY',
    'LA',
    'LB',
    'LC',
    'LK',
    'LR',
    'MH',
    'ML',
    'MM',
    'MN',
    'MP',
    'MS',
    'MU',
    'MV',
    'MW',
    'MX',
    'MY',
    'MZ',
    'NG',
    'NI',
    'NP',
    'PA',
    'PE',
    'PK',
    'PR',
    'PS',
    'PW',
    'PY',
    'RW',
    'SL',
    'SO',
    'SR',
    'SS',
    'SV',
    'SX',
    'SY',
    'TC',
    'TH',
    'TJ',
    'TL',
    'TM',
    'TO',
    'TT',
    'TZ',
    'UG',
    'US',
    'UY',
    'UZ',
    'VC',
    'VE',
    'VG',
    'VI',
    'VN',
    'WS',
    'YE',
    'ZM',
    'ZW',
  ],
  UZS: ['UZ'],
  VUV: ['VU'],
  WST: ['WS'],
  XAF: ['CF', 'CG', 'CM', 'TD'],
  XOF: ['BJ', 'CI', 'ML', 'SN'],
  XPF: ['NC', 'PF', 'WF'],
  ZAR: ['ZA'],
};

export const BDC_COUNTRY_CURRENCIES: Record<string, string[]> = Object.entries(
  BDC_CURRENCY_COUNTRIES,
).reduce<Record<string, string[]>>((acc, [currency, codes]) => {
  codes.forEach((code) => {
    (acc[code] ??= []).push(currency);
  });

  return acc;
}, {});

export interface IBdcChargeField {
  key: string;
  field: keyof PropertyFeeModel;
  isPercentage: boolean;
}

export interface IBdcChargeEntry {
  key: string;
  amount: number;
  isPercentage: boolean;
}

export const BDC_CHARGE_FIELDS: IBdcChargeField[] = [
  { key: 'cleaning_fee', field: 'cleaningFee', isPercentage: false },
  {
    key: 'per_booking_percentage_tax',
    field: 'perBookingPercentageTax',
    isPercentage: true,
  },
  {
    key: 'per_booking_amount_tax',
    field: 'perBookingAmountTax',
    isPercentage: false,
  },
  { key: 'per_night_tax', field: 'perNightTax', isPercentage: false },
  {
    key: 'per_person_per_night_tax',
    field: 'perPersonPerNightTax',
    isPercentage: false,
  },
];

export function getBdcCharges(
  propertyFee: PropertyFeeModel | null | undefined,
): IBdcChargeEntry[] {
  if (!propertyFee) {
    return [];
  }

  return BDC_CHARGE_FIELDS.filter(({ field }) => {
    const amount = propertyFee[field] as number;

    return amount && amount > 0;
  }).map(({ key, field, isPercentage }) => ({
    key,
    amount: propertyFee[field] as number,
    isPercentage,
  }));
}

export interface IBdcDiscountField {
  key: string;
  field: keyof PropertyFeeModel;
}

export interface IBdcDiscountEntry {
  key: string;
  amount: number;
}

export const BDC_DISCOUNT_FIELDS: IBdcDiscountField[] = [
  { key: 'weekly_discount', field: 'weeklyDiscount' },
  { key: 'monthly_discount', field: 'monthlyDiscount' },
];

export function getBdcDiscounts(
  propertyFee: PropertyFeeModel | null | undefined,
): IBdcDiscountEntry[] {
  if (!propertyFee) {
    return [];
  }

  return BDC_DISCOUNT_FIELDS.map(({ key, field }) => ({
    key,
    amount: (propertyFee[field] as number) || 0,
  }));
}

export const BDC_EXTRANET_URL = 'https://admin.booking.com/';
